import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

const CONFIRM_WORD = 'DELETE';

interface DeleteAccountDialogProps {
  open: boolean;
  /** Name shown in the prompt so the admin can see who they are about to remove. */
  subjectName: string;
  /** "traveler" | "guide" — used in the copy only. */
  subjectKind: string;
  isDeleting: boolean;
  /** Server message from a failed attempt (open bookings, admin account, ...). */
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for the super-admin delete action. Deletion is irreversible from
 * the UI, so it asks the admin to type DELETE rather than relying on a single
 * misclickable button.
 */
export function DeleteAccountDialog({
  open,
  subjectName,
  subjectKind,
  isDeleting,
  errorMessage,
  onCancel,
  onConfirm,
}: DeleteAccountDialogProps) {
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    if (open) setConfirmText('');
  }, [open]);

  const canConfirm = confirmText.trim().toUpperCase() === CONFIRM_WORD && !isDeleting;

  return (
    <Dialog open={open} onClose={isDeleting ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Delete {subjectKind}?</DialogTitle>
      <DialogContent>
        <DialogContentText component="div" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>{subjectName}</strong> will lose access immediately and disappear from the
            platform. Past bookings, payments and reviews are kept for records.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This cannot be undone from the admin panel.
          </Typography>
        </DialogContentText>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          autoFocus
          fullWidth
          size="small"
          label={`Type ${CONFIRM_WORD} to confirm`}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          disabled={isDeleting}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} disabled={isDeleting}>
          Cancel
        </Button>
        <Button color="error" variant="contained" onClick={onConfirm} disabled={!canConfirm}>
          {isDeleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
